import { useCallback, useEffect, useState } from "react";

import { BUCKET_FICHAJES, fichajes } from "../services/puerto";
import FotoPrivada from "../components/funcionales/FotoPrivada";
import {
  agrupar,
  diaAR,
  diaSemana,
  duracionTexto,
  fechaCorta,
  huecos,
  minutosDia,
  semanaDe,
  semanaEnCurso,
  totalSemanaMin,
} from "../shared/puerto";
import Loading from "./Loading";

/* El reloj de fichaje del puerto.
 *
 * ── ESTA PANTALLA MUESTRA, NO JUZGA ────────────────────────────────────────
 *
 * Las dos personas de puerto trabajan **a demanda**: no hay horario esperado
 * contra el cual comparar, así que «llegó tarde» no es algo que el sistema
 * pueda calcular — y no hace falta que lo calcule. Anota, y el que juzga es
 * quien sabe qué se había acordado ese día.
 *
 * ── Y NO SE CARGA DESDE ACÁ ────────────────────────────────────────────────
 *
 * El fichaje entra por WhatsApp con la foto del molinete. Un botón acá sería un
 * segundo lugar para lo mismo, y el día que alguien fiche en el que no es ese
 * día no aparece en el parte del lunes. Es la lección de los dos talleres.
 *
 * ── LAS DOS HORAS VAN JUNTAS Y ETIQUETADAS ─────────────────────────────────
 *
 * La grande es cuándo llegó el mensaje —eso lo sabe el servidor y no se puede
 * falsear— y la chica es lo que marcaba el molinete en la foto. El sistema
 * puede afirmar «a las 09:12 alguien mandó una foto de un molinete que marcaba
 * 09:07»; no puede afirmar «entró a las 09:07». Mostrar una sola sería hacer
 * pasar una por la otra.
 *
 * ── ABRE EN LA SEMANA EN CURSO ─────────────────────────────────────────────
 *
 * El mail de los lunes cierra la semana anterior porque es un informe; esta
 * pantalla se abre para ver qué pasó HOY. Abrirla en la semana cerrada
 * mostraría un registro viejo justo cuando alguien entra a comprobar que el
 * fichaje de esta mañana quedó.
 */

/* El botón vive en `FotoPrivada`, compartido con los trabajos del puerto: con
 * una copia acá, el día que cambie cómo se dice «no se pudo firmar» las dos
 * pantallas contestarían distinto sobre la misma clase de problema. */
const Foto = ({ ruta }) => (
  <FotoPrivada ruta={ruta} bucket={BUCKET_FICHAJES} />
);

const Marca = ({ marca }) => {
  if (!marca) {
    // EL HUECO SE DIBUJA. Una celda vacía y una jornada sin salida se leen
    // igual si no se dice cuál es cuál.
    return <span className="sin-dato">—</span>;
  }
  return (
    <>
      <b>{marca.hora}</b>
      {/* LA HORA DEL MOLINETE SE MUESTRA SIEMPRE que se pudo leer, aunque
          coincida con la del mensaje. Ocultarla cuando coincidía hacía que
          «lo leyó y daba lo mismo» se viera igual que «no lo pudo leer» —y así
          un fichaje con el molinete tomado parecía no tenerlo. Cuando coinciden
          se dice «coincide», que es un dato, no ruido: significa que la foto
          respalda la hora del mensaje. */}
      {marca.foto && marca.foto !== marca.hora && (
        <span className="sub">molinete {marca.foto}</span>
      )}
      {marca.foto && marca.foto === marca.hora && (
        <span className="sub">molinete {marca.foto} · coincide</span>
      )}
      {!marca.foto && (
        <span className="sub sin-dato">sin hora del molinete</span>
      )}
      <span className="sub">
        <Foto ruta={marca.archivo} />
      </span>
    </>
  );
};

const PuertoFichaje = () => {
  const [filas, setFilas] = useState(null);
  const [error, setError] = useState(null);
  /* `false` es la semana en curso y `true` la cerrada, que es la del mail. Dos
   * opciones y no un calendario: la pregunta de esta pantalla es «esta semana»
   * o «la del último parte», y un rango libre invita a mirar cualquier cosa. */
  const [cerrada, setCerrada] = useState(false);

  const rango = cerrada ? semanaDe() : semanaEnCurso();

  const traer = useCallback(async () => {
    setFilas(null);
    setError(null);
    try {
      setFilas(await fichajes(rango.desde.toISOString(), rango.hasta.toISOString()));
    } catch (e) {
      setError(e.message || "No se pudieron leer los fichajes");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cerrada]);

  useEffect(() => {
    traer();
  }, [traer]);

  if (error && !filas) {
    return (
      <div className="panel-aviso"><h3>No se pudo consultar</h3></div>
    );
  }
  if (!filas) return <Loading />;

  const gente = agrupar(filas);
  const sinCerrar = huecos(gente);
  const desde = fechaCorta(diaAR(rango.desde.toISOString()));
  const hasta = fechaCorta(
    diaAR(new Date(rango.hasta.getTime() - 86400000).toISOString()),
  );

  return (
    <div className="docs">
      <div className="tk-filtros">
        <button
          className={"tk-chip" + (!cerrada ? " activo" : "")}
          onClick={() => setCerrada(false)}
        >
          Esta semana
        </button>
        <button
          className={"tk-chip" + (cerrada ? " activo" : "")}
          onClick={() => setCerrada(true)}
        >
          La del último parte
        </button>
        <span className="tk-item-meta">
          {desde} al {hasta}
        </span>
      </div>

      {/* LOS HUECOS SE CUENTAN, y es lo único accionable de esta pantalla. Un
          registro incompleto no es un error del sistema: es alguien que se
          olvidó de fichar, y eso se arregla hablando con esa persona. */}
      {sinCerrar > 0 && (
        <p className="table-escapan">
          <b>{sinCerrar}</b> {sinCerrar === 1 ? "día" : "días"} sin la entrada o
          sin la salida.
        </p>
      )}

      {gente.length === 0 && (
        <p className="panel-aviso suave">
          Ningún fichaje en {cerrada ? "esa" : "esta"} semana.
        </p>
      )}

      {gente.map((p) => (
        <div key={p.numero} className="pf-bloque">
          <h3>
            {p.persona}
            {p.rol && <span className="tk-item-meta"> {p.rol}</span>}
            {/* EL ACUMULADO DE LA SEMANA, al lado del nombre: es la pregunta de
                Nico. Suma sólo los días completos; si no hubo ninguno, «—».
                El rótulo sigue a la semana elegida: con «La del último parte»
                lo que se suma es la semana anterior, no ésta. */}
            <span className="tk-item-meta">
              {" "}· trabajó {cerrada ? "esa" : "esta"} semana: <b>{duracionTexto(totalSemanaMin(p))}</b>
            </span>
          </h3>
          <div className="tabla-wrap">
            <table className="tabla fija">
              <thead>
                <tr>
                  <th>Día</th>
                  <th>Entrada</th>
                  <th>Salida</th>
                  <th>Trabajó</th>
                </tr>
              </thead>
              <tbody>
                {p.dias.map((d) => (
                  <tr key={d.fecha}>
                    <td>
                      {/* EL DÍA DE LA SEMANA adelante (Nico): «Lun 08/09». */}
                      <b>{diaSemana(d.fecha)}</b> {fechaCorta(d.fecha)}
                      {/* LOS DE MÁS NO SE DESCARTAN. Tres entradas el mismo día
                          es justo lo que hay que mirar; quedarse con la primera
                          lo escondería. */}
                      {d.sueltos.length > 0 && (
                        <span className="sub sin-dato">
                          además:{" "}
                          {d.sueltos.map((m) => `${m.tipo} ${m.hora}`).join(" · ")}
                        </span>
                      )}
                    </td>
                    <td><Marca marca={d.entrada} /></td>
                    <td><Marca marca={d.salida} /></td>
                    {/* El tiempo entre entrada y salida (por la hora del
                        mensaje). Un día con hueco no muestra total: «—». */}
                    <td className="dato">{duracionTexto(minutosDia(d))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}

      <p className="docs-pie">Se ficha por WhatsApp, con la foto del molinete.</p>
    </div>
  );
};

export default PuertoFichaje;
